/*
 * Kasa Önü Fırsatları — sonsuz akan ama dokunulabilir ürün şeridi.
 *
 * Neden transform/CSS animasyonu değil: transform ile kayan bir şeritte müşteri
 * karta dokunduğunda hedef kayıyor, tıklama ıskalanıyor ve parmakla manuel
 * kaydırma hiç mümkün olmuyor. Bunun yerine şerit native yatay scroll konteyneri
 * olarak bırakılıp scrollLeft yavaşça artırılıyor: kartlar her an tıklanabilir,
 * müşteri istediği an parmağıyla kaydırabilir, dokunduğunda akış durup el
 * çekilince kaldığı yerden devam eder.
 */
const TY_PREOFFERS_RESUME_DELAY_MS = 2200;

class TyPreoffersMarquee {
  constructor(track) {
    this.track = track;
    this.rows = Array.from(track.querySelectorAll('.ty-cart-preoffers__track-row'));
    this.rafId = null;
    this.resumeTimer = null;
    this.lastTs = 0;
    this.userHold = false;
    this.dragging = false;
    this.dragMoved = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.visible = true;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.hold = this.hold.bind(this);
    this.releaseSoon = this.releaseSoon.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onWheel = this.onWheel.bind(this);

    this.bind();
  }

  bind() {
    const t = this.track;
    // Dokunma / fare ile tutma → akışı durdur
    ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach((evt) =>
      t.addEventListener(evt, this.hold, { passive: true })
    );
    // Bırakınca kısa bir gecikmeyle devam et (müşteri okumaya devam ediyor olabilir)
    ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'focusout'].forEach((evt) =>
      t.addEventListener(evt, this.releaseSoon, { passive: true })
    );
    t.addEventListener('mouseenter', this.hold, { passive: true });
    t.addEventListener('mouseleave', this.releaseSoon, { passive: true });
    t.addEventListener('pointerdown', this.onPointerDown);
    t.addEventListener('pointermove', this.onPointerMove);
    t.addEventListener('pointerup', this.onPointerUp);
    t.addEventListener('pointercancel', this.onPointerUp);
    t.addEventListener('click', this.onClick, true);
    t.addEventListener('wheel', this.onWheel, { passive: false });

    // Sepet kapalıyken boşuna dönmesin
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((e) => e.isIntersecting);
        },
        { threshold: 0 }
      );
      this.io.observe(t);
    }
  }

  hold() {
    this.userHold = true;
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  }

  releaseSoon() {
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.userHold = false;
      this.resumeTimer = null;
    }, TY_PREOFFERS_RESUME_DELAY_MS);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('button, a, input, select, textarea, label, summary')) return;
    this.dragging = true;
    this.dragMoved = false;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.hold();
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    const distance = event.clientX - this.dragStartX;
    if (!this.dragMoved && Math.abs(distance) <= 6) return;
    if (!this.dragMoved) {
      this.dragMoved = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }
    this.track.scrollLeft = this.dragStartScrollLeft - distance;
    this.normalize();
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.dragPointerId = null;
    this.releaseSoon();
  }

  onClick(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  onWheel(event) {
    if (window.innerWidth < 990 || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
    const canScroll = this.track.scrollWidth > this.track.clientWidth + 1;
    if (!canScroll) return;
    this.hold();
    this.track.scrollLeft += event.deltaY;
    this.normalize();
    event.preventDefault();
    this.releaseSoon();
  }

  // İlk satırın genişliği + aradaki boşluk = bir tur mesafesi
  loopWidth() {
    if (this.rows.length < 2) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return this.rows[0].getBoundingClientRect().width + gap;
  }

  // Manuel kaydırmada da sonsuz hissi versin diye iki uçta da başa sarıyoruz
  normalize() {
    const loop = this.loopWidth();
    if (loop <= 0) return;
    if (this.track.scrollLeft >= loop) this.track.scrollLeft -= loop;
    else if (this.track.scrollLeft < 0) this.track.scrollLeft += loop;
  }

  destroy() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
    if (this.io) this.io.disconnect();
  }
}

// Sepet çekmecesi güncellendiğinde içerik innerHTML ile baştan basıldığı için
// (inline script'ler bu durumda çalışmaz) kurulumu buradan tetikliyoruz.
function tyInitPreoffersMarquee(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('.ty-cart-preoffers__track').forEach((track) => {
    if (track.dataset.tyMarquee === '1') return;
    track.dataset.tyMarquee = '1';
    new TyPreoffersMarquee(track);
  });
}

let tyCartShippingProofTimer = null;

function tyUpdateCartShippingProof(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  const proofs = scope.querySelectorAll('[data-cart-shipping-proof]');
  if (!proofs.length) return;

  const istanbulNow = new Date(Date.now() + 3 * 60 * 60 * 1000);
  const day = istanbulNow.getUTCDay();
  const minutes = istanbulNow.getUTCHours() * 60 + istanbulNow.getUTCMinutes();

  proofs.forEach((proof) => {
    const weekdayCutoff = Number.parseInt(proof.dataset.cutoff, 10) || 930;
    const saturdayCutoff = Number.parseInt(proof.dataset.cutoffSat, 10) || 780;
    const cutoffFor = (dayOfWeek) => {
      if (dayOfWeek === 0) return null;
      return dayOfWeek === 6 ? saturdayCutoff : weekdayCutoff;
    };
    const todayCutoff = cutoffFor(day);
    const shipsToday = todayCutoff !== null && minutes < todayCutoff;
    const shipDate = new Date(Date.UTC(
      istanbulNow.getUTCFullYear(),
      istanbulNow.getUTCMonth(),
      istanbulNow.getUTCDate()
    ));

    if (!shipsToday) {
      do shipDate.setUTCDate(shipDate.getUTCDate() + 1);
      while (cutoffFor(shipDate.getUTCDay()) === null);
    }

    let message = proof.dataset.today;
    if (!shipsToday) {
      const difference = Math.round((shipDate.getTime() - Date.UTC(
        istanbulNow.getUTCFullYear(),
        istanbulNow.getUTCMonth(),
        istanbulNow.getUTCDate()
      )) / 86400000);
      if (difference === 1) {
        message = proof.dataset.tomorrow;
      } else {
        const locale = document.documentElement.lang || 'tr-TR';
        const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(shipDate);
        message = (proof.dataset.dayTemplate || '__DAY__').replace('__DAY__', weekday);
      }
    }

    const output = proof.querySelector('[data-cart-shipping-message]');
    if (output) output.textContent = message;
    proof.setAttribute('aria-label', `${message}. ${proof.dataset.fast || ''}`.trim());
  });
}

function tyInitCartShippingProof(root) {
  tyUpdateCartShippingProof(root);
  if (tyCartShippingProofTimer !== null || !document.querySelector('[data-cart-shipping-proof]')) return;
  tyCartShippingProofTimer = window.setInterval(() => tyUpdateCartShippingProof(), 60000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    tyInitPreoffersMarquee();
    tyInitCartShippingProof();
  });
} else {
  tyInitPreoffersMarquee();
  tyInitCartShippingProof();
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeTimer = null;
    this.handleDocumentKeydown = (event) => {
      if (event.code === 'Escape' && this.classList.contains('active')) this.close();
    };

    this.addEventListener('click', (evt) => {
      const panel = this.querySelector('.ty-cart-panel');
      if (panel && !panel.contains(evt.target)) this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    const wasActive = this.classList.contains('active');
    const focusSource = triggeredBy || document.activeElement;
    if (
      focusSource &&
      focusSource !== document.body &&
      !this.contains(focusSource) &&
      (triggeredBy || !wasActive)
    ) {
      this.setActiveElement(focusSource);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);

    this.classList.add('animate', 'active');
    this.animateShippingProgress();
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'true');
    document.querySelectorAll('[aria-controls="CartDrawer"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    if (!document.body.classList.contains('cart-drawer-open')) {
      this.savedScrollY = window.scrollY;
      document.body.style.top = `-${this.savedScrollY}px`;
    }
    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    document.addEventListener('keydown', this.handleDocumentKeydown);

    requestAnimationFrame(() => {
      const focusTarget = document.getElementById('CartDrawer') || this.querySelector('.drawer__inner');
      const focusEl = this.querySelector('.ty-cart-panel__back') || this.querySelector('.drawer__close');
      if (focusTarget && typeof trapFocus === 'function') trapFocus(focusTarget, focusEl);
    });
  }

  animateShippingProgress() {
    const fill = this.querySelector('.ty-free-shipping__bar-fill[data-progress]');
    if (!fill) return;

    const progress = Math.min(100, Math.max(0, Number(fill.dataset.progress) || 0));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fill.style.width = `${progress}%`;
      return;
    }

    if (this.shippingProgressFrame) cancelAnimationFrame(this.shippingProgressFrame);
    fill.style.transition = 'none';
    fill.style.width = '0%';
    fill.getBoundingClientRect();
    this.shippingProgressFrame = requestAnimationFrame(() => {
      fill.style.transition = '';
      fill.style.width = `${progress}%`;
      this.shippingProgressFrame = null;
    });
  }

  close() {
    this.classList.remove('active');
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'false');
    document.querySelectorAll('[aria-controls="CartDrawer"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.closeTimer = setTimeout(() => {
      if (!this.classList.contains('active')) this.classList.remove('animate');
      this.closeTimer = null;
    }, reduceMotion ? 0 : 350);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    const returnFocus = this.activeElement && this.activeElement.isConnected && !this.contains(this.activeElement)
      ? this.activeElement
      : cartLink;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(returnFocus);
    else if (returnFocus) returnFocus.focus({ preventScroll: true });
    this.activeElement = null;
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    document.body.style.top = '';
    if (typeof this.savedScrollY === 'number') {
      window.scrollTo(0, this.savedScrollY);
      this.savedScrollY = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.id)
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const section = this.getSectionsToRender()[0];
    const sectionHtml = parsedState.sections?.[section.id];
    if (typeof sectionHtml !== 'string' || sectionHtml.length === 0) {
      return this.refreshContents(parsedState, section);
    }

    this.productId = parsedState.id;

    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!el) return;
        const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (html != null) el.innerHTML = html;
      });
    }

    if (typeof parsedState.item_count === 'number') {
      this.updateCartCount(parsedState.item_count);
    } else {
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js')
        .then((r) => r.json())
        .then((cart) => this.updateCartCount(cart.item_count))
        .catch(() => {});
    }

    const itemsEl = this.querySelector('.ty-cart-panel__items');
    const emptyState = this.querySelector('.ty-cart-panel__empty-state');
    const footerEl = this.querySelector('#CartDrawer-Footer');
    const drawerItems = this.querySelector('cart-drawer-items');

    const hasItems = Boolean(itemsEl && itemsEl.querySelector('.ty-cart-item'));

    this.classList.toggle('is-empty', !hasItems);
    if (drawerItems) drawerItems.classList.toggle('is-empty', !hasItems);
    if (emptyState) emptyState.classList.toggle('hidden', hasItems);
    if (itemsEl) itemsEl.classList.toggle('hidden', !hasItems);
    if (footerEl) footerEl.classList.toggle('hidden', !hasItems);

    tyInitPreoffersMarquee(this);
    tyInitCartShippingProof(this);
    if (window.tmLazy && typeof window.tmLazy.initImageLazyLoad === 'function') {
      window.tmLazy.initImageLazyLoad();
    }

    this.open();
  }

  refreshContents(parsedState, section = this.getSectionsToRender()[0]) {
    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';

    return fetch(`${cartUrl}${separator}section_id=${encodeURIComponent(section.id)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart drawer section could not be loaded');
        return response.text();
      })
      .then((html) => {
        if (!html) throw new Error('Cart drawer section is empty');
        this.renderContents({
          ...parsedState,
          sections: { ...(parsedState.sections || {}), [section.id]: html },
        });
      })
      .catch(() => {
        window.location.href = cartUrl;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
  }

  updateCartCount(count) {
    document
      .querySelectorAll('.ty-header__action--cart .ty-header__cart-count, #cart-count, [data-bnav-cart-count]')
      .forEach((countEl) => {
        countEl.textContent = count;
        if (countEl.hasAttribute('data-bnav-cart-count')) countEl.hidden = count === 0;
      });

    const bubbleSpan = document.querySelector('.cart-count-bubble span[aria-hidden]');
    if (bubbleSpan) bubbleSpan.textContent = count;

    const bubble = document.querySelector('.cart-count-bubble');
    if (bubble) bubble.style.display = count > 0 ? '' : 'none';

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { item_count: Number(count) || 0 } }));
  }

  setActiveElement(element) {
    if (element && element !== document.body && !this.contains(element)) this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      if ((event.source === 'product-form' || event.source === 'card-atc') && event.cartData && event.cartData.sections) return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
